<template>
  <el-select
    :value="value"
    :placeholder="placeholder"
    :style="selectStyle"
    :popper-class="popperClass"
    :class="selectClass"
    filterable
    remote
    clearable
    reserve-keyword
    :multiple="multiple"
    :multiple-limit="multipleLimit"
    :collapse-tags="collapseTags"
    :remote-method="handleSearch"
    :loading="loading"
    @input="handleInput"
    @visible-change="handleVisibleChange"
  >
    <el-option
      v-for="option in mergedOptions"
      :key="optionValue(option)"
      :label="optionLabel(option)"
      :value="optionValue(option)"
      :disabled="isOptionDisabled(option)"
    >
      <span class="common-option-text" :title="optionLabel(option)">{{ optionLabel(option) }}</span>
    </el-option>
    <div class="common-select-pager" @mousedown.prevent @click.stop>
      <el-button
        size="mini"
        :disabled="page <= 1 || loading"
        @click.stop="changePage(page - 1)"
      >上一页</el-button>
      <span class="common-select-pager__text">第 {{ page }} / {{ totalPages }} 页（每页{{ pageSize }}条）</span>
      <el-button
        size="mini"
        :disabled="page >= totalPages || loading"
        @click.stop="changePage(page + 1)"
      >下一页</el-button>
    </div>
  </el-select>
</template>

<script>
import api from '@/services/api';

function readByPath(source, path, fallback) {
  if (!path) return source == null ? fallback : source;
  const segments = String(path)
    .split('.')
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  let current = source;
  for (const segment of segments) {
    if (current == null) return fallback;
    current = current[segment];
  }
  return current == null ? fallback : current;
}

function normalizeQueryParams(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return {};
  return { ...params };
}

export default {
  name: 'UtwlCommonSelect',
  props: {
    value: {
      type: [String, Number, Array],
      default: ''
    },
    url: {
      type: String,
      required: true
    },
    queryParams: {
      type: Object,
      default: () => ({})
    },
    selectedOptions: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '请输入关键词搜索'
    },
    width: {
      type: [String, Number],
      default: 200
    },
    pageSize: {
      type: Number,
      default: 10
    },
    popperClass: {
      type: String,
      default: 'utwl-common-select-popper'
    },
    errorMessage: {
      type: String,
      default: '加载选项失败'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    multipleLimit: {
      type: Number,
      default: 0
    },
    collapseTags: {
      type: Boolean,
      default: false
    },
    searchParam: {
      type: String,
      default: 'keyword'
    },
    valueKey: {
      type: String,
      default: 'id'
    },
    labelKey: {
      type: String,
      default: 'name'
    },
    responseListPath: {
      type: String,
      default: 'data.data.list'
    },
    responseTotalPath: {
      type: String,
      default: 'data.data.total'
    },
    responsePagePath: {
      type: String,
      default: 'data.data.page'
    },
    responsePageSizePath: {
      type: String,
      default: 'data.data.pageSize'
    },
    labelFormatter: {
      type: Function,
      default: null
    },
    optionDisabled: {
      type: Function,
      default: null
    },
    beforeRequest: {
      type: Function,
      default: null
    },
    afterResponse: {
      type: Function,
      default: null
    },
    selectClass: {
      type: [String, Object, Array],
      default: ''
    }
  },
  data() {
    return {
      options: [],
      optionMap: {},
      loading: false,
      keyword: '',
      page: 1,
      total: 0,
      initialized: false
    };
  },
  computed: {
    selectStyle() {
      const width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      return width ? { width } : null;
    },
    totalPages() {
      const total = Number(this.total || 0);
      const size = Number(this.pageSize || 10);
      return Math.max(1, Math.ceil(total / size));
    },
    mergedOptions() {
      const merged = [];
      const seen = new Set();
      const pushOption = (option) => {
        const value = this.optionValue(option);
        if (value === '' || seen.has(value)) return;
        seen.add(value);
        merged.push(option);
      };
      const currentValues = Array.isArray(this.value)
        ? this.value.map((item) => String(item == null ? '' : item).trim()).filter(Boolean)
        : this.value != null && this.value !== ''
          ? [String(this.value).trim()].filter(Boolean)
          : [];
      currentValues.forEach((value) => {
        pushOption(this.optionMap[value]);
      });
      (this.selectedOptions || []).forEach(pushOption);
      (this.options || []).forEach(pushOption);
      return merged;
    }
  },
  watch: {
    selectedOptions: {
      immediate: true,
      handler(options) {
        this.registerOptions(options);
      }
    }
  },
  methods: {
    optionValue(option) {
      const value = readByPath(option, this.valueKey, '');
      return value == null ? '' : String(value).trim();
    },
    optionLabel(option) {
      if (typeof this.labelFormatter === 'function') {
        return String(this.labelFormatter(option) || '').trim();
      }
      const label = readByPath(option, this.labelKey, '');
      return label == null ? '' : String(label).trim();
    },
    isOptionDisabled(option) {
      return typeof this.optionDisabled === 'function' ? Boolean(this.optionDisabled(option)) : false;
    },
    registerOptions(options = []) {
      const nextMap = { ...this.optionMap };
      options.forEach((option) => {
        const value = this.optionValue(option);
        if (!value) return;
        nextMap[value] = option;
      });
      this.optionMap = nextMap;
    },
    selectedOptionList(value = this.value) {
      const values = Array.isArray(value)
        ? value.map((item) => String(item == null ? '' : item).trim()).filter(Boolean)
        : value != null && value !== ''
          ? [String(value).trim()].filter(Boolean)
          : [];
      return values
        .map((item) => this.optionMap[item])
        .filter((item) => item && typeof item === 'object');
    },
    handleInput(value) {
      this.$emit('input', value);
      this.$emit('change', value);
      this.$emit('selected-options-change', this.selectedOptionList(value));
    },
    buildRequestPayload(page = 1, keyword = this.keyword) {
      const nextKeyword = String(keyword || '').trim();
      const payload = {
        page: Math.max(1, Number(page || 1)),
        pageSize: this.pageSize,
        ...normalizeQueryParams(this.queryParams)
      };
      if (nextKeyword) {
        payload[this.searchParam || 'keyword'] = nextKeyword;
      }
      if (typeof this.beforeRequest === 'function') {
        const customPayload = this.beforeRequest({
          payload: { ...payload },
          keyword: nextKeyword,
          page: payload.page,
          pageSize: this.pageSize
        });
        return customPayload && typeof customPayload === 'object' ? customPayload : payload;
      }
      return payload;
    },
    parseResponse(response, fallbackPage) {
      if (typeof this.afterResponse === 'function') {
        const custom = this.afterResponse(response);
        if (custom && typeof custom === 'object') {
          return {
            list: Array.isArray(custom.list) ? custom.list : [],
            total: Number(custom.total || 0) || 0,
            page: Number(custom.page || fallbackPage) || fallbackPage,
            pageSize: Number(custom.pageSize || this.pageSize) || this.pageSize
          };
        }
      }
      return {
        list: Array.isArray(readByPath(response, this.responseListPath, [])) ? readByPath(response, this.responseListPath, []) : [],
        total: Number(readByPath(response, this.responseTotalPath, 0) || 0) || 0,
        page: Number(readByPath(response, this.responsePagePath, fallbackPage) || fallbackPage) || fallbackPage,
        pageSize: Number(readByPath(response, this.responsePageSizePath, this.pageSize) || this.pageSize) || this.pageSize
      };
    },
    async loadOptions(page = 1, keyword = this.keyword) {
      this.loading = true;
      try {
        const requestPage = Math.max(1, Number(page || 1));
        const requestKeyword = String(keyword || '').trim();
        const payload = this.buildRequestPayload(requestPage, requestKeyword);
        const response = await api.post(this.url, payload);
        const parsed = this.parseResponse(response, requestPage);
        this.page = parsed.page;
        this.total = parsed.total;
        this.keyword = requestKeyword;
        this.options = Array.isArray(parsed.list) ? parsed.list : [];
        this.registerOptions(this.options);
        this.$emit('selected-options-change', this.selectedOptionList(this.value));
        this.initialized = true;
      } catch (error) {
        console.error(this.errorMessage, error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch(query) {
      this.keyword = String(query || '').trim();
      this.loadOptions(1, this.keyword);
    },
    changePage(page) {
      const nextPage = Math.max(1, Number(page || 1));
      if (nextPage === this.page) return;
      this.loadOptions(nextPage, this.keyword);
    },
    handleVisibleChange(visible) {
      if (!visible) return;
      if (!this.initialized || !Array.isArray(this.options) || this.options.length === 0) {
        this.loadOptions(1, this.keyword);
      }
    },
    refresh() {
      return this.loadOptions(this.page, this.keyword);
    },
    reset() {
      this.keyword = '';
      this.page = 1;
      this.total = 0;
      this.options = [];
      this.initialized = false;
    }
  }
};
</script>

<style>
.utwl-common-select-popper {
  width: 350px !important;
  max-width: 350px !important;
}

.utwl-common-select-popper .el-select-dropdown__list {
  max-width: 100%;
}

.utwl-common-select-popper .el-select-dropdown__item {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.utwl-common-select-popper .common-option-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.utwl-common-select-popper .common-select-pager {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

.utwl-common-select-popper .common-select-pager__text {
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}
</style>
